/**
 * LiveFareBar — a persistent, always-visible LIVE fare strip shown inside the
 * Dispatch driver panel during a trip.
 *
 * WHY: the driver navigates in the external Google Maps/Waze app (a web app can't
 * overlay that), so the realistic win is to make the running fare ALWAYS visible
 * the moment they're back in Dispatch OS — without having to switch to the METER
 * tab. This bar shows on the Navigation tab; tapping it jumps to the full
 * FareMeterPanel breakdown (one tap away). The NAV/METER toggle stays untouched.
 *
 * SCOPE: frontend/UI only. Polls the SAME read-only /driver/live-fare endpoint
 * the meter uses. No fare-calc / dispatch / lifecycle changes.
 */
import React, { useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";

import { api } from "../../lib/api";

const POLL_MS = 5000;

export function LiveFareBar({ rideId, fallbackEstimate, onExpand }) {
  const [liveFare, setLiveFare] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    let active = true;
    const fetchFare = async () => {
      if (!rideId) return;
      try {
        const res = await api.get(`/driver/live-fare/${rideId}`);
        if (active) setLiveFare(res?.data?.live_fare ?? null);
      } catch (e) {
        // before started_at / transient — keep last good value.
      }
    };
    fetchFare();
    pollRef.current = setInterval(fetchFare, POLL_MS);
    return () => {
      active = false;
      clearInterval(pollRef.current);
    };
  }, [rideId]);

  const isLive = liveFare != null;
  const amount = isLive
    ? Number(liveFare)
    : fallbackEstimate != null
    ? Number(fallbackEstimate)
    : null;

  return (
    <button
      type="button"
      onClick={onExpand}
      className="w-full flex items-center justify-between gap-2 bg-[#0A0A0A] border-2 border-[#10B981] px-3 py-2.5 hover:bg-[#1F1F1F] transition-colors"
      data-testid="live-fare-bar"
      aria-label="Show full fare breakdown"
    >
      <span className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full bg-[#22C55E] ${isLive ? "pulse-active" : "animate-pulse"}`} />
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF]">
          {isLive ? "Live fare" : "Est. fare"}
        </span>
      </span>
      <span
        className="font-led text-[#22C55E] text-2xl leading-none"
        style={{ textShadow: "0 0 1px rgba(34,197,94,0.9), 0 0 6px rgba(34,197,94,0.35)" }}
        data-testid="live-fare-bar-amount"
      >
        {amount != null ? `£${amount.toFixed(2)}` : "£—"}
      </span>
      <span className="flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF]">
        Breakdown <ChevronRight className="w-3.5 h-3.5" />
      </span>
    </button>
  );
}

export default LiveFareBar;
